import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound } from "lucide-react";
import { api } from "@/lib/api";

const newKey = () => btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

const STEPS = [
  "Tap “Generate a new key” below. It's made in your browser and never sent to CAMRYN. Copy it into your password manager as “CAMRYN master key, [month year]”, and keep the offline copy too.",
  "Open Deployments → Re-publish → Secrets tab → View & edit. Copy the current DATA_KEK value into DATA_KEK_PREVIOUS (it's there already, set to “none”). Then replace the DATA_KEK value with the new key. Check both for typos, then Save and Re-publish.",
  "Come back here after the publish. “Members on the current key” should equal the total, and the fingerprint should have changed. If the app doesn't come back up, DATA_KEK_PREVIOUS is probably missing or mistyped: put the old key back into DATA_KEK and Re-publish. Nothing is lost.",
  "Wait 14 days (backup restores use the last 7 days). Then set DATA_KEK_PREVIOUS back to none in Secrets and Re-publish. Finally, delete the old key from your password manager and destroy its offline copy. Backups older than that become unreadable for deleted members.",
];

function Generator() {
  const [k, setK] = useState("");
  const copy = () => navigator.clipboard.writeText(k).then(() => toast.success("Key copied. Save it in your password manager now."));
  return k ? (
    <div className="rounded-xl bg-stone-50 p-3 space-y-2" data-testid="key-generated">
      <p className="text-xs text-stone-600">New key (shown once; it disappears when you leave this page):</p>
      <code className="block break-all text-xs" data-testid="key-generated-value">{k}</code>
      <button onClick={copy} data-testid="key-copy-btn" className="btn-ink h-11 px-4 rounded-full text-sm font-semibold inline-flex items-center gap-2"><Copy className="h-4 w-4" />Copy key</button>
    </div>
  ) : (
    <button onClick={() => setK(newKey())} data-testid="key-generate-btn" className="h-11 px-4 rounded-full border border-stone-300 text-sm font-semibold">Generate a new key</button>
  );
}

export function KeyRotation() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/owner/key-status").then(({ data }) => setS(data)).catch(() => setS(false)); }, []);
  if (!s) return null;
  const rows = [["Key fingerprint", s.fingerprint], ["In use since", `${fmt(s.since)} (${s.days} days)`],
    ["Members on the current key", `${s.members - s.pending} of ${s.members}`], ["Previous key still loaded", s.previous_loaded ? `Yes (${s.previous_fingerprint})` : "No"]];
  return (
    <section className="card p-5" data-testid="owner-key-rotation">
      <h2 className="font-display text-xl flex items-center gap-2"><KeyRound className="h-5 w-5" />Master encryption key</h2>
      <p className={`text-sm mt-1 font-semibold ${s.due || s.pending ? "text-[#C86D51]" : "text-emerald-700"}`} data-testid="key-status-line">
        {s.pending ? `${s.pending} member keys still need moving. Check the publish finished.` : s.due ? "Monthly rotation is due." : "Up to date. Next rotation in " + (30 - s.days) + " days."}
      </p>
      {s.previous_loaded && (
        <p className="mt-2 text-sm rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-amber-900" data-testid="key-remove-previous">
          {s.days >= 14 ? "It's been 14 days: set DATA_KEK_PREVIOUS back to none, re-publish, then destroy the old key."
            : `Old key still loaded. On ${fmt(new Date(new Date(s.since).getTime() + 14 * 864e5).toISOString())}, set DATA_KEK_PREVIOUS back to none, re-publish, then destroy the old key.`}
        </p>
      )}
      <dl className="mt-3 divide-y divide-stone-100 text-sm">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3 py-2"><dt className="text-stone-600">{k}</dt><dd className="font-semibold text-right" data-testid={`key-${k.split(" ")[0].toLowerCase()}`}>{v}</dd></div>)}
      </dl>
      <button onClick={() => setOpen(!open)} data-testid="key-steps-toggle" className="tap mt-2 text-sm font-semibold text-[#C86D51]">{open ? "Hide" : "Show"} the rotation checklist</button>
      {open && (
        <div className="mt-3 space-y-3" data-testid="key-steps">
          <ol className="list-decimal pl-5 space-y-2 text-sm text-stone-700">{STEPS.map((t) => <li key={t}>{t}</li>)}</ol>
          <p className="text-xs text-stone-500">Never paste a key into a chat or email. If DATA_KEK is lost while members' keys still need it, their data can't be recovered.</p>
          <Generator />
        </div>
      )}
    </section>
  );
}
